import { useEffect, useState } from "react";
import { getAllLocations } from "services/locationService";

const OTHER_TOWN = "Autres";

// Lowercase, accent-free form used to compare towns.
const normalize = (s = "") =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const byFr = (a, b) => a.localeCompare(b, "fr", { sensitivity: "base" });

/**
 * Groups locations by town: [{ town, locations }], towns in alphabetical order,
 * locations sorted by name then table (flagged `sharedName` when two share a name). "Montreal" and "Montréal " land in the
 * same group; locations without a town go to a final "Autres" group.
 */
export const groupLocationsByTown = (locations) => {
  const groups = new Map();
  for (const loc of locations) {
    const town = loc.town?.trim() || OTHER_TOWN;
    const key = loc.town?.trim() ? normalize(town) : "\uffff";
    if (!groups.has(key)) groups.set(key, { town, locations: [] });
    groups.get(key).locations.push(loc);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => byFr(a, b))
    .map(([, g]) => {
      const names = g.locations.map((l) => normalize(l.name || ""));
      return {
        ...g,
        locations: g.locations
          // sharedName: another location of this town has the same name (show its table to tell them apart)
          .map((l, i) => ({ ...l, sharedName: names.filter((n) => n === names[i]).length > 1 }))
          .sort((a, b) => byFr(a.name || "", b.name || "") || byFr(a.table || "", b.table || "")),
      };
    });
};

// Active, non-discarded locations, loaded once per mount.
export const useActiveLocations = () => {
  const [locations, setLocations] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getAllLocations()
      .then((all) => !cancelled && setLocations(all.filter((loc) => loc.active === true)))
      .catch((error) => {
        console.error("Error fetching locations:", error);
        if (!cancelled) setLocations([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return locations;
};
